/* ═══════════════════════════════════════════════════════════════════════════
   WHAT THE BUSINESS PAYS US

   There are two quite different flows of money on this platform, they both
   involve Stripe, and confusing them is the single most expensive mistake
   this screen can make.

     ONE.  The customer pays the business.
           Vipps, Stripe, wallet, invoice — the rails in domain/payments.js,
           configured with THE BUSINESS'S OWN merchant keys. The money goes
           from the customer to the business and no part of it passes through
           Avtale.net.

     TWO.  The business pays Avtale.net.
           A monthly charge for having a rail switched on at all. This one
           runs on AVTALE.NET'S Stripe account, which the tenant never sees
           and never configures — they are the customer here, not the
           merchant. Stripe does the onboarding and takes the money.

   Both say "Stripe" and they are not the same Stripe. A tenant who pastes
   their own Stripe key in and believes they have therefore paid their
   platform bill has made a reasonable mistake about an unreasonable screen,
   so every surface that touches this file has to keep the two apart in
   words, not only in code. `WHOSE` below exists to be printed.

   ── Priced per rail ───────────────────────────────────────────────────────

   Each rail is subscribed to separately, so a business that only ever sends
   invoices pays for invoices and not for a card terminal it does not use.
   One line per rail on the bill, and a tenant can drop one without dropping
   the others.

   ── The figures ───────────────────────────────────────────────────────────

   Not here. The monthly price of each rail is a commercial decision and this
   file will not invent one: a number typed into a demo has a way of turning
   into a number on a quote, and a quote is a promise. `PRICE` holds null
   until the real figures are set, every screen renders an unset price as
   unset, and `priced()` is what tells a caller whether there is anything
   honest to display. The mechanism is complete and the numbers are somebody
   else's to supply.
   ═══════════════════════════════════════════════════════════════════════ */

/** Whose Stripe is whose. Printed on screen, not just consulted in code. */
export const WHOSE = {
  rails: 'your own Stripe account — your customers’ money, paid to you',
  platform: 'Avtale.net’s Stripe account — your subscription, paid to us',
};

/**
 * Monthly price per rail, in kroner, excluding VAT.
 *
 * null means "not decided yet", which is a truthful state and renders as one.
 * It does NOT mean free, and `priced()` is the guard that stops a screen
 * implying it does.
 */
export const PRICE = {
  vipps: null,
  stripe: null,
  wallet: null,
  invoice: null,
};

/** Is there a real figure for this rail? */
export const priced = railId => Number.isFinite(PRICE[railId]);

/** What to print where a price goes. Never "0", never "Free". */
export function priceWords(railId) {
  return priced(railId)
    ? `${PRICE[railId].toLocaleString('nb-NO')} kr a month`
    : 'Price not set yet';
}

/* ── What is actually subscribed to ───────────────────────────────────────

   Chamcore's own shape, read out of `Payments::subscription` and
   `StripeService::clientSubscription`: a PACKAGE plus ADDONS, and the two
   become one Stripe subscription with one subscription item each.
   `ApiController::createOrUpdateClientSubscription` then adds and removes
   individual items as a tenant switches services on and off — it does not
   create a second subscription.

   That matters here because it is also the answer to "one line per rail,
   priced separately": the line is a subscription ITEM, not a subscription.
   A tenant drops invoices and keeps Vipps by removing one item, and the
   monthly bill is one bill.

   The plans carry no figures for the same reason `PRICE` holds null, and
   Chamcore's own file says it out loud in a comment beside its addon table:
   *these prices should ideally come from your DB/config, NOT from the
   frontend.* This file is the frontend. */

export const PLANS = [
  {
    id: 'page',
    label: 'Page and booking',
    what: 'Your own page on Avtale.net, the booking calendar, the customer '
        + 'record and a place in the marketplace.',
  },
  {
    id: 'full',
    label: 'Page, booking and the console',
    what: 'Everything above, plus the rota, the price list, campaigns and '
        + 'the document room.',
  },
];

export const plan = id => PLANS.find(p => p.id === id) || null;

/**
 * The payload Chamcore's subscription route takes.
 *
 * No prices in it, deliberately — see above. The server prices the article
 * numbers it is given, which is the only way a tenant cannot pay a figure
 * they chose themselves.
 */
export function order({ planId, rails = [] } = {}) {
  return {
    package: planId ? { id: planId, label: plan(planId)?.label || planId } : null,
    /* `article_no` is Chamcore's own key for a priced line, the one
       `createOrUpdateClientSubscription` looks `stripe_products` up by. */
    addons: rails.map(id => ({ id, article_no: 'rail-' + id, active: true, quantity: 1 })),
  };
}

/* ── The subscription ─────────────────────────────────────────────────────
   One per rail per business. */

export const STATES = ['none', 'onboarding', 'active', 'past_due', 'cancelled'];

export const MEANING = {
  none: 'Not subscribed. The rail cannot take money.',
  onboarding: 'Started at Stripe and not finished. The rail cannot take money yet.',
  active: 'Paid and running.',
  past_due: 'The monthly payment failed. The rail is switched off.',
  cancelled: 'Ended. The rail is switched off.',
};

/**
 * May this rail take money right now?
 *
 * Active and nothing else. A failed monthly payment switches the rail off the
 * same day — which is the decision that was made, and it is the strict one,
 * so the screens that show it have to be unambiguous rather than gentle.
 */
export const usable = sub => sub?.state === 'active';

/**
 * What a failed payment does NOT do.
 *
 * Switching a rail off stops NEW bookings on it. It does not abandon the
 * authorisations already made on it: money held against somebody's card
 * belongs to that customer until it is captured or released, and a platform
 * that walked away from a hold because a tenant's own bill bounced would be
 * holding a stranger's money over a dispute they are not party to.
 *
 * So existing holds settle on the rail they were made on, whatever has
 * happened to the subscription since. This function is what the sweep and
 * the cancellation screen ask.
 */
export const maySettleExisting = () => true;

/** The one-line consequence, for the screen that is about to switch it off. */
export function consequence(railId, openHolds = 0) {
  const holds = openHolds
    ? ` The ${openHolds} authorisation${openHolds === 1 ? '' : 's'} already held `
      + `${openHolds === 1 ? 'is' : 'are'} unaffected — ${openHolds === 1 ? 'it' : 'they'} `
      + 'can still be captured or released.'
    : '';
  return `New bookings that need ${railId === 'invoice' ? 'an invoice' : 'a payment'} `
       + `will not be offered this rail.${holds}`;
}

/**
 * The next thing the tenant has to do, given where the subscription stands.
 * Returns `{ do, say }` — the button and the sentence above it.
 */
export function step(sub) {
  switch (sub?.state) {
    case 'active':
      return { do: 'cancel', say: 'Running. Cancel it and the rail stops taking money.' };
    case 'onboarding':
      return { do: 'resume', say: 'You started at Stripe and did not finish. Pick it up where you left it.' };
    case 'past_due':
      return { do: 'fix', say: 'The last monthly payment failed, so the rail is off. Update the card at Stripe.' };
    case 'cancelled':
      return { do: 'start', say: 'Ended. Subscribing again switches the rail back on.' };
    default:
      return { do: 'start', say: 'Not subscribed. The rail is configured but cannot take money.' };
  }
}

/**
 * Where the tenant goes to pay us.
 *
 * Stripe's own hosted Checkout, on Avtale.net's account. The tenant's card
 * details never touch this platform for the same reason their customers'
 * never do — it is somebody else's page, under somebody else's certificate,
 * and neither of us wants to be in PCI scope over a subscription.
 *
 * The shape is the shape a real Checkout Session URL has, so the screen that
 * shows it does not change the day there is one.
 */
export function checkoutLink(businessId, railId) {
  return `#/subscribe/${encodeURIComponent(businessId)}/${encodeURIComponent(railId)}`;
}

/** The billing portal, where a card is updated and a subscription ended.
    Also Stripe's, for the same reason. */
export function portalLink(businessId) {
  return `#/billing/${encodeURIComponent(businessId)}`;
}

/** The whole bill, for the rails that are on. Returns `{ lines, total,
    unpriced }` — and `total` is null when anything on it has no figure,
    because a total that quietly omits a line is worse than no total. */
export function bill(subs = {}) {
  const lines = Object.entries(subs)
    .filter(([, s]) => s?.state === 'active')
    .map(([railId]) => ({ railId, price: PRICE[railId], words: priceWords(railId) }));
  const unpriced = lines.filter(l => !Number.isFinite(l.price)).length;
  return {
    lines,
    unpriced,
    total: unpriced ? null : lines.reduce((n, l) => n + l.price, 0),
  };
}
